/**
 * MH-24 Bảng điện tử công cộng — danh sách thiết bị và chi tiết một bảng.
 *
 * Danh sách theo mẫu trang Cụm loa (dùng lại ô tóm tắt, ô Kết nối, khối âm lượng, đếm ngược làm mới
 * của MH-02) nhưng dữ liệu và lệnh từ API bảng tin: `devices/status` (thiết bị + DV016 mới nhất +
 * lịch phát mới nhất), `devices/summary` (ba ô tóm tắt), `devices/status/export` (Excel). Chọn bảng
 * để thao tác:
 *   - Phát ngay (`broadcast/now`, DV010 một khung từ bây giờ); tích "Phát khẩn cấp" trong hộp thì
 *     gửi `broadcast/emergency` (DV011 — chen ngang, KHÔNG hủy lịch);
 *   - Điều khiển (`devices/commands`, DV017): âm lượng, dừng / bỏ qua / tạm dừng / phát tiếp, khởi
 *     động lại, nâng cấp. Lệnh bật/tắt màn hình (MaLenh 1) CHƯA làm theo quyết định của tỉnh.
 *
 * Gộp kho (29/10/2026): bản tin trong hộp Phát ngay lấy từ Kho nội dung `GET /api/v1/bulletins`
 * (`trangThai=da_duyet,dang_phat,da_phat`, `loaiBanTin=1,2,3,4`), gửi `id` uuid `ban_tin.id`.
 *
 * Chi tiết ghép thuộc tính RIÊNG của bảng (hướng màn hình, kích thước điểm ảnh, ảnh chụp gần nhất)
 * từ `/api/v1/digital-signage/devices/:id/attributes`.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viBangTin } from '../ngon-ngu/vi-bang-tin';
import { thietBiApi, type DongThietBi, type NutDiaBan } from '../api/thiet-bi-api';
import { bangDienTuApi, type ThuocTinhBangTin } from '../api/bang-dien-tu-api';
import { banTinApi, type BoLocBanTin, type DongBanTin } from '../api/ban-tin-api';
import {
  bangTinApi,
  dangKetNoiBT,
  type BangTinApi,
  type KetQuaGuiBT,
  type MaLenhBT,
  type TomTatThietBiBT,
  type TrangDuLieu,
  type TrangThaiLocBT,
  type TrangThaiThietBiBT,
} from '../api/bang-tin-api';
import { dungTaiDuLieu, dungTuLamMoi } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from '../thanh-phan/TrangThaiManHinh';
import { NhanTrangThai } from '../thanh-phan/NhanTrangThai';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { hienThongBaoNhanh } from '../thanh-phan/ThongBaoNhanh';
import { gioNgayVn } from './NhatKyLichPhatThietBi';
import { DaiThongBao, loiThanhChuoi, thongBaoKetQuaGui } from './BangTinChung';
import { HopPhatKhanCapBangTin, type ApiPhatKhanCapBT } from './HopPhatKhanCapBangTin';
import { MenuDieuKhienThietBi, type MaNutDieuKhien } from './MenuDieuKhienThietBi';
import {
  ChonDonViHanhChinh,
  DemNguocLamMoi,
  KhoiAmLuong,
  OKetNoiThietBi,
  OThoiDiem,
  OTomTatThietBi,
  mangDungLuong,
} from './MH02DanhSachThietBi';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import './danh-sach-lich-phat.css';
import './bien-tap.css';
import './bang-tin.css';

const t = viBangTin.thietBi;
const tc = viBangTin.chung;

export type ApiThietBiBT = Pick<
  BangTinApi,
  'trangThaiThietBi' | 'tomTatThietBi' | 'xuatDanhSachThietBi' | 'phatNgay' | 'phatKhanCap' | 'dieuKhien'
> & {
  cayDiaBan: (signal?: AbortSignal) => Promise<NutDiaBan[]>;
  /** Kho nội dung `GET /api/v1/bulletins` (gộp kho bản tin). */
  banTinDanhSach: (tham: BoLocBanTin, signal?: AbortSignal) => Promise<TrangDuLieu<DongBanTin>>;
};

export const API_THIET_BI_BT_MAC_DINH: ApiThietBiBT = {
  ...bangTinApi,
  cayDiaBan: (signal) => thietBiApi.cayDiaBan({}, signal),
  banTinDanhSach: banTinApi.danhSach,
};

/** Mã địa bàn (không trùng) của các bảng đã chọn. */
function diaBanCuaBang(ds: TrangThaiThietBiBT[]): string[] {
  return [...new Set(ds.map((d) => d.nguonId).filter((x): x is string => !!x))];
}

/* ------------------------------ Kiểm tham số DV017 ------------------------------ */

/** Kiểm và chuẩn hoá ThamSo theo MaLenh (CV276 mục 10); sai trả `{loi}`. */
export function thamSoLenh(maLenh: MaLenhBT, giaTri: string): { thamSo: string } | { loi: string } {
  const v = giaTri.trim();
  if (maLenh === '0') {
    const n = Number(v);
    return /^\d+$/.test(v) && n >= 0 && n <= 100 ? { thamSo: String(n) } : { loi: t.amLuongLoi };
  }
  if (maLenh === '2') return ['0', '1', '2', '3'].includes(v) ? { thamSo: v } : { loi: t.canChon };
  if (maLenh === '3') return /^\d+$/.test(v) ? { thamSo: String(Number(v)) } : { loi: t.treLoi };
  // ThamSo của DV017 tối đa 50 ký tự (CV276) — máy chủ cũng chặn ở cùng mức.
  return /^https?:\/\/\S+$/.test(v) && v.length <= 50 ? { thamSo: v } : { loi: t.urlLoi };
}

/**
 * Lệnh DV017 (CV276 mục 10) trên menu Điều khiển của bảng điện tử — cùng mã lệnh với cụm loa:
 * khởi động lại (3), bật/tắt loa (0 = âm lượng), phát tiếp / tạm dừng / bản tin tiếp theo / dừng phát
 * tất cả (2 với tham số 3/2/1/0). Không có: nâng cấp (MaLenh 4, tỉnh chưa làm), bật/tắt công suất
 * (MaLenh 1 của bảng tin là bật/tắt màn hình — chưa làm giai đoạn này).
 */
const NUT_DIEU_KHIEN_BANG_TIN: readonly MaNutDieuKhien[] = [
  'khoiDongLai',
  'batLoa',
  'tatLoa',
  'phatTiep',
  'tamDung',
  'banTinTiepTheo',
  'dungPhatTatCa',
];

/* --------------------------------- Danh sách --------------------------------- */

export interface MH24DanhSachProps {
  api?: ApiThietBiBT;
  /** API của hộp Phát khẩn cấp (mặc định: Kho nội dung + bảng tin). */
  apiKhanCap?: ApiPhatKhanCapBT;
}

const DS_TRANG_THAI_LOC: TrangThaiLocBT[] = ['dang_ket_noi', 'dang_phat', 'mat_ket_noi'];

/** Nội dung đang phát (DV016 NoiDungPhat) dạng mảng. */
function mangNoiDungPhat(d: TrangThaiThietBiBT): string[] {
  const nd = d.noiDungPhat;
  if (!nd) return [];
  return Array.isArray(nd) ? nd : [nd];
}

/**
 * Dòng bảng tin → khuôn dòng cụm loa để dùng chung ô "Kết nối" của trang Cụm loa (cột sóng, đồng bộ
 * trung ương, đang phát, kênh). Bảng tin chưa đồng bộ trung ương (giai đoạn sau).
 */
function dongKieuCumLoa(d: TrangThaiThietBiBT): DongThietBi {
  const ketNoi = dangKetNoiBT(d);
  const dangPhat = ketNoi && mangNoiDungPhat(d).length > 0;
  return {
    trangThaiHienThi: !ketNoi ? 'mat_ket_noi' : dangPhat ? 'dang_phat' : 'cho_phat',
    dongBoTrungUong: false,
    kenhKetNoi: d.phuongThucKetNoi === 'gian_tiep' ? 'api' : 'mqtt',
    phuongThucKetNoi: d.phuongThucKetNoi === 'gian_tiep' ? 'gian_tiep' : 'truc_tiep',
  } as DongThietBi;
}

/** "[giờ ngày]-Tên lịch", đỏ kèm "Lịch phát chưa cập nhật" khi bảng chưa báo DV014 (như mẫu). */
function OLichMoiNhat({ l }: { l: TrangThaiThietBiBT['lichPhatMoiNhat'] }): ReactNode {
  if (!l) {
    return (
      <span className="bt-lich-chua" data-lich="chua-co">
        {t.chuaCoLich} - {t.lichChuaCapNhat}
      </span>
    );
  }
  const chu = `[${gioNgayVn(l.thoiDiem)}]-${l.tenLichPhat}`;
  return l.daCapNhat ? (
    <span title={l.lichPhatId} data-lich="da-cap-nhat">
      {chu}
    </span>
  ) : (
    <span className="bt-lich-chua" title={l.lichPhatId} data-lich="chua-cap-nhat">
      {chu} - {t.lichChuaCapNhat}
    </span>
  );
}

/**
 * MH-24 phần 1: danh sách bảng điện tử theo mẫu trang Cụm loa — lọc địa bàn / trạng thái, Xuất Excel;
 * ba ô tóm tắt; tìm mã thiết bị / mã nhà cung cấp; âm lượng (DV017 MaLenh 0); Phát theo lịch,
 * Điều khiển, Phát ngay; bảng có ô chọn; tự làm mới 60 giây.
 */
export function MH24DanhSachBangTin({
  api = API_THIET_BI_BT_MAC_DINH,
  apiKhanCap,
}: MH24DanhSachProps = {}): ReactNode {
  const phien = dungPhien();
  const tl = viThietBi.danhSach;
  const [nguonId, datNguonId] = useState('');
  const [trangThaiLoc, datTrangThaiLoc] = useState<TrangThaiLocBT | ''>('');
  const [oMa, datOMa] = useState('');
  const [oNcc, datONcc] = useState('');
  const [tim, datTim] = useState({ keyword: '', maNhaCungCap: '' });
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState<number>(SO_DONG_MAC_DINH);
  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const [hop, datHop] = useState<'phat' | null>(null);
  const [thongBao, datThongBao] = useState<{ loai: 'ok' | 'loi'; noiDung: string } | null>(null);
  const [dangXuat, datDangXuat] = useState(false);

  /** Địa bàn mặc định là phạm vi người dùng, như trang Cụm loa. */
  const nguonLoc = nguonId || phien.nguonIdHienTai || '';
  const boLoc = {
    nguonId: nguonLoc || undefined,
    keyword: tim.keyword || undefined,
    maNhaCungCap: tim.maNhaCungCap || undefined,
    trangThaiHienThi: trangThaiLoc || undefined,
  };
  const dangLoc = nguonId !== '' || trangThaiLoc !== '' || tim.keyword !== '' || tim.maNhaCungCap !== '';

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  /** Đích Phát khẩn cấp khi không tích bảng: địa bàn đang xem, không có thì cả tỉnh. */
  const diaBanPhat = nguonLoc || phien.hoSo?.maTinh || '';
  const tenDiaBanPhat =
    (cay.duLieu ?? []).find((n) => n.maDinhDanh === diaBanPhat)?.ten ??
    (diaBanPhat === phien.hoSo?.maTinh ? (phien.hoSo?.tenTinh ?? diaBanPhat) : diaBanPhat);

  const kq = dungTaiDuLieu<TrangDuLieu<TrangThaiThietBiBT>>({
    tai: (signal) => api.trangThaiThietBi({ ...boLoc, page: trang, pageSize: soDong }, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonLoc, trangThaiLoc, tim.keyword, tim.maNhaCungCap],
  });

  const tomTat = dungTaiDuLieu<TomTatThietBiBT>({
    tai: (signal) => api.tomTatThietBi({ nguonId: nguonLoc || undefined }, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonLoc],
  });

  /** Tự làm mới 60 giây; dừng khi tab ẩn, đang chọn dòng hoặc đang mở hộp. */
  const lamMoi = dungTuLamMoi(
    () => {
      kq.taiLai();
      tomTat.taiLai();
    },
    { dangChonDong: daChon.size > 0, dangMoNgan: hop !== null },
  );

  const dsDong = useMemo(() => kq.duLieu?.items ?? [], [kq.duLieu]);
  const bangChon = dsDong.filter((d) => daChon.has(d.deviceId));
  /** Âm lượng chung của các bảng đang chọn (như trang Cụm loa); khác nhau hoặc chưa rõ thì null. */
  const amLuongChung = useMemo(() => {
    const ds = bangChon.map((d) => d.amLuong).filter((x): x is number => typeof x === 'number');
    return ds.length > 0 && ds.length === bangChon.length && ds.every((x) => x === ds[0])
      ? (ds[0] as number)
      : null;
  }, [bangChon]);
  const batDauStt = (trang - 1) * soDong;
  const duocQuanLy = phien.coQuyen('signage.manage');
  const bayGio = Math.floor(Date.now() / 1000);

  const doiLoc = (): void => {
    datTrang(1);
    datDaChon(new Set());
  };
  const xoaLoc = (): void => {
    datNguonId('');
    datTrangThaiLoc('');
    datOMa('');
    datONcc('');
    datTim({ keyword: '', maNhaCungCap: '' });
    doiLoc();
  };

  const xong = (macDinh: string) => (r: KetQuaGuiBT | null) => {
    datHop(null);
    const tb = thongBaoKetQuaGui(r, macDinh);
    datThongBao(tb);
    if (tb.loai === 'ok') hienThongBaoNhanh(tb.noiDung);
    kq.taiLai();
  };

  const xuatExcel = async (): Promise<void> => {
    datDangXuat(true);
    try {
      const r = await api.xuatDanhSachThietBi(boLoc);
      hienThongBaoNhanh(dienVao(tl.daXuatExcel, { so: r.soDong }));
    } catch (e) {
      hienThongBaoNhanh(dienVao(tl.loiXuatExcel, { loi: loiThanhChuoi(e) }), 'loi');
    } finally {
      datDangXuat(false);
    }
  };

  /** Khối âm lượng dùng chung với trang Cụm loa: gửi DV017 MaLenh 0 cho các bảng đã chọn. */
  const guiAmLuong: typeof thietBiApi.guiLenh = async ({ maLenh, thamSo, deviceIds }) => {
    const bang = dsDong.filter((d) => deviceIds.includes(d.deviceId));
    const r = await api.dieuKhien({
      bangDienTu: bang.map((d) => d.bangDienTuId),
      diaBan: diaBanCuaBang(bang),
      maLenh: maLenh as MaLenhBT,
      thamSo: thamSo ?? '',
    });
    const loi = r?.loi ?? [];
    const maLoi = new Set(loi.map((l) => l.bangDienTuId));
    return {
      tong: bang.length,
      thanhCong: bang
        .filter((d) => !maLoi.has(d.bangDienTuId))
        .map((d) => ({ cumLoaId: d.bangDienTuId, deviceId: d.deviceId, moc: 'da_gui' })),
      dangCho: [],
      loi: loi.map((l) => ({
        cumLoaId: l.bangDienTuId ?? '',
        deviceId: bang.find((d) => d.bangDienTuId === l.bangDienTuId)?.deviceId ?? '',
        maLoi: l.maLoi,
        moTaLoi: l.thongBao,
      })),
      soGianTiep: 0,
    };
  };

  const cot: CotBang<TrangThaiThietBiBT>[] = [
    { khoa: 'stt', nhan: tl.cot.stt, canGiua: true, ve: (d) => batDauStt + dsDong.indexOf(d) + 1 },
    {
      khoa: 'ketNoi',
      nhan: tl.cot.ketNoi,
      ve: (d) => (
        <span data-ket-noi={dangKetNoiBT(d) ? 'dang-ket-noi' : 'mat-ket-noi'}>
          <OKetNoiThietBi d={dongKieuCumLoa(d)} />
        </span>
      ),
    },
    { khoa: 'diaBan', nhan: tl.cot.diaBan, ve: (d) => d.tenDiaBan ?? d.nguonId ?? tc.khongCo },
    {
      khoa: 'ten',
      nhan: tl.cot.tenThietBi,
      ve: (d) => (
        <>
          <Link to={`/bang-tin-dien-tu/${encodeURIComponent(d.deviceId)}`}>
            {d.tenThietBi ?? d.bangDienTuId}
          </Link>
          <span className="tb-ma-chip">{d.bangDienTuId}</span>
        </>
      ),
    },
    {
      khoa: 'amLuong',
      nhan: tl.cot.amLuong,
      canGiua: true,
      // Âm lượng bảng tự báo trong DV016 (`ThongTinThietBi.AmLuong`, ngoài CV276); không gửi thì "—".
      ve: (d) =>
        typeof d.amLuong === 'number' ? String(d.amLuong) : <span title={t.amLuongKhongBao}>—</span>,
    },
    { khoa: 'lich', nhan: tl.cot.lichPhatMoiNhat, ve: (d) => <OLichMoiNhat l={d.lichPhatMoiNhat ?? null} /> },
    {
      khoa: 'capNhat',
      nhan: tl.cot.capNhatLuc,
      ve: (d) => (
        <OThoiDiem
          epochGiay={typeof d.thoiDiem === 'number' ? d.thoiDiem : null}
          tuoiGiay={typeof d.thoiDiem === 'number' ? bayGio - d.thoiDiem : null}
        />
      ),
    },
    {
      khoa: 'ncc',
      nhan: tl.cot.maNhaCungCap,
      canGiua: true,
      ve: (d) => <span title={d.tenNhaCungCap ?? undefined}>{d.maNhaCungCap || tc.khongCo}</span>,
    },
    {
      khoa: 'nhietDo',
      nhan: tl.cot.nhietDo,
      canGiua: true,
      ve: (d) => (typeof d.nhietDo === 'number' ? `${d.nhietDo} ${tl.doC}` : tc.khongCo),
    },
    {
      khoa: 'mang',
      nhan: tl.cot.mangDungLuong,
      // Mất kết nối thì ghi "Mất kết nối" thay kiểu mạng cũ, như mẫu.
      ve: (d) =>
        mangDungLuong({
          trangThaiKetNoi: dangKetNoiBT(d) ? (d.trangThaiKetNoi ?? null) : null,
          dungLuongSuDungKb: d.dungLuongSuDungKb ?? null,
        }),
    },
    { khoa: 'phienBan', nhan: tl.cot.phienBan, ve: (d) => d.phienBanUngDung ?? tc.khongCo },
  ];

  return (
    <section className="man-hinh tb-danh-sach" data-man-hinh="MH-24">
      <h1 className="chi-cho-doc-man-hinh">{t.tieuDe}</h1>

      <div className="tb-hang tb-hang--giua-hai-dau">
        <div className="tb-loc-gon" data-vung="thanh-loc">
          <ChonDonViHanhChinh
            id="tbbt-loc-dia-ban"
            anNhan
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            onDoi={(v) => {
              datNguonId(v);
              doiLoc();
            }}
          />
          <select
            id="tbbt-loc-trang-thai"
            aria-label={tl.trangThai}
            value={trangThaiLoc}
            onChange={(e) => {
              datTrangThaiLoc(e.target.value as TrangThaiLocBT | '');
              doiLoc();
            }}
          >
            <option value="">{tl.chonTrangThai}</option>
            {DS_TRANG_THAI_LOC.map((s) => (
              <option key={s} value={s}>
                {t.trangThaiLoc[s]}
              </option>
            ))}
          </select>
          {dangLoc ? (
            <button type="button" className="nut" onClick={xoaLoc} data-hanh-dong="xoa-loc">
              {vi.bang.xoaLoc}
            </button>
          ) : null}
        </div>
        <div className="tb-nut-phai">
          <button
            type="button"
            className="nut tb-nut-excel"
            data-hanh-dong="xuat-excel"
            disabled={dangXuat}
            onClick={() => void xuatExcel()}
          >
            {dangXuat ? tl.dangXuatExcel : tl.xuatExcel}
          </button>
        </div>
      </div>

      <OTomTatThietBi
        loai="bang_tin_dien_tu"
        duLieu={tomTat.duLieu ? { ...tomTat.duLieu, chuaKichHoat: 0 } : null}
        coLoi={tomTat.trangThai === 'loi'}
      />

      <div className="tb-hang-thao-tac" data-vung="hang-thao-tac">
        <form
          className="tb-nhom-tim"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            datTim({ keyword: oMa.trim(), maNhaCungCap: oNcc.trim() });
            doiLoc();
          }}
        >
          <input
            id="tbbt-tim-ma"
            type="search"
            aria-label={tl.maThietBi}
            placeholder={tl.maThietBiGoiY}
            value={oMa}
            onChange={(e) => datOMa(e.target.value)}
          />
          <input
            id="tbbt-tim-ncc"
            type="search"
            aria-label={tl.nhaCungCap}
            placeholder={t.maNccGoiY}
            value={oNcc}
            onChange={(e) => datONcc(e.target.value)}
          />
          <button type="submit" className="nut nut--chinh" data-hanh-dong="tim-kiem">
            <BieuTuong ten="tim-kiem" co={16} />
            {tl.timKiem}
          </button>
        </form>
        <KhoiAmLuong
          ids={bangChon.map((d) => d.deviceId)}
          giaTriBanDau={amLuongChung}
          guiLenh={guiAmLuong}
          choPhep={duocQuanLy}
          onXong={kq.taiLai}
        />
        {duocQuanLy ? (
          <div className="tb-nhom-nut" data-vung="thanh-cong-cu">
            <Link
              className="nut nut--chinh"
              to="/bang-tin-dien-tu/lich-phat/moi"
              data-hanh-dong="phat-theo-lich"
            >
              <BieuTuong ten="lich" co={16} />
              {tl.phatTheoLich}
            </Link>
            {/* Menu lệnh giống trang Cụm loa; gửi DV017 qua `guiAmLuong` (mọi mã lệnh). */}
            <MenuDieuKhienThietBi
              thietBi={bangChon.map((d) => ({
                id: d.deviceId,
                phuongThucKetNoi: d.phuongThucKetNoi === 'gian_tiep' ? 'gian_tiep' : 'truc_tiep',
              }))}
              choPhep={duocQuanLy}
              guiLenh={guiAmLuong}
              onXong={kq.taiLai}
              dsNut={NUT_DIEU_KHIEN_BANG_TIN}
              tieuDeMenu={t.tieuDeMenuDieuKhien}
            />
            <button
              type="button"
              className="nut nut--nguy-hiem"
              data-hanh-dong="phat-ngay"
              onClick={() => datHop('phat')}
            >
              <BieuTuong ten="phat" co={14} />
              {t.phatNgay}
            </button>
          </div>
        ) : null}
      </div>

      <DaiThongBao tb={thongBao} />

      <BangDuLieu<TrangThaiThietBiBT>
        nhan={t.tieuDe}
        cot={cot}
        dong={dsDong}
        khoaDong={(d) => d.deviceId}
        nhanDong={(d) => dienVao(t.chonDong, { ten: d.tenThietBi ?? d.bangDienTuId })}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        daChon={duocQuanLy ? daChon : undefined}
        onDoiChon={duocQuanLy ? datDaChon : undefined}
        anThanhHangLoat
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />

      <DemNguocLamMoi soLanTai={kq.soLanTai} dangTamDung={lamMoi.dangTamDung} />

      {hop === 'phat' ? (
        <HopPhatKhanCapBangTin
          bangChon={bangChon}
          diaBan={diaBanPhat ? [diaBanPhat] : []}
          tenDiaBan={tenDiaBanPhat}
          onDong={() => datHop(null)}
          onXong={xong(t.daPhatNgay)}
          api={apiKhanCap}
        />
      ) : null}
    </section>
  );
}

export interface MH24ChiTietProps {
  api?: Pick<typeof thietBiApi, 'chiTiet'>;
  bangDienTuGoi?: Pick<typeof bangDienTuApi, 'thuocTinhChiTiet' | 'thuocTinhCapNhat' | 'anhChupTaiLen'>;
}

/** MH-24 phần 2: chi tiết một bảng tin — thông tin chung + thuộc tính riêng. */
export function MH24ChiTietBangTin({ api, bangDienTuGoi }: MH24ChiTietProps = {}): ReactNode {
  const goi = api ?? thietBiApi;
  const bdtGoi = bangDienTuGoi ?? bangDienTuApi;
  const { id = '' } = useParams();
  const phien = dungPhien();
  const [tab, datTab] = useState<'chung' | 'anhChup'>('chung');
  const [dangSua, datDangSua] = useState(false);
  const [thongBao, datThongBao] = useState<string | null>(null);

  const thietBi = dungTaiDuLieu<DongThietBi>({
    tai: (signal) => goi.chiTiet(id, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
  });

  const thuocTinh = dungTaiDuLieu<ThuocTinhBangTin>({
    tai: (signal) => bdtGoi.thuocTinhChiTiet(id, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
  });

  const [huongManHinh, datHuongManHinh] = useState<'ngang' | 'doc' | ''>('');
  const [chieuRongPx, datChieuRongPx] = useState('');
  const [chieuCaoPx, datChieuCaoPx] = useState('');

  const batDauSua = (): void => {
    datHuongManHinh(thuocTinh.duLieu?.huongManHinh ?? '');
    datChieuRongPx(thuocTinh.duLieu?.chieuRongPx ? String(thuocTinh.duLieu.chieuRongPx) : '');
    datChieuCaoPx(thuocTinh.duLieu?.chieuCaoPx ? String(thuocTinh.duLieu.chieuCaoPx) : '');
    datDangSua(true);
  };

  const luuThuocTinh = async (): Promise<void> => {
    await bdtGoi.thuocTinhCapNhat(id, {
      huongManHinh: huongManHinh || null,
      chieuRongPx: chieuRongPx ? Number(chieuRongPx) : null,
      chieuCaoPx: chieuCaoPx ? Number(chieuCaoPx) : null,
    });
    datDangSua(false);
    datThongBao(vi.bangDienTu.chiTiet.daLuu);
    thuocTinh.taiLai();
  };

  if (thietBi.trangThai === 'dang-tai' || thuocTinh.trangThai === 'dang-tai') {
    return <KhungXuong soDong={6} soCot={2} taiLau={thietBi.taiLau || thuocTinh.taiLau} />;
  }
  if (thietBi.trangThai === 'loi' && thietBi.loi) {
    return <TrangThaiLoi loi={thietBi.loi} onThuLai={thietBi.taiLai} />;
  }
  if (thuocTinh.trangThai === 'loi' && thuocTinh.loi) {
    return <TrangThaiLoi loi={thuocTinh.loi} onThuLai={thuocTinh.taiLai} />;
  }
  const tb = thietBi.duLieu;
  const tt = thuocTinh.duLieu;
  if (!tb || !tt) return <TrangThaiRong moTa={vi.trangThaiManHinh.rongMoTa} />;

  const nhanHuong =
    tt.huongManHinh === 'ngang'
      ? vi.bangDienTu.huongNgang
      : tt.huongManHinh === 'doc'
        ? vi.bangDienTu.huongDoc
        : vi.bangDienTu.huongChuaKhaiBao;

  return (
    <section className="man-hinh" data-man-hinh="MH-24-chi-tiet">
      <nav className="vet-duong-dan" aria-label={vi.dieuHuong.bangTinDienTu}>
        <Link to="/bang-tin-dien-tu">{vi.bangDienTu.chiTiet.ve}</Link> /{' '}
        <span>{tb.tenThietBi ?? tb.cumLoaId}</span>
      </nav>
      <header className="man-hinh__dau">
        <h1>{tb.tenThietBi ?? tb.cumLoaId}</h1>
        <p className="man-hinh__trang-thai">
          <NhanTrangThai ma={tb.trangThaiHienThi} /> — {nhanHuong}
        </p>
        <div className="man-hinh__hanh-dong">
          {phien.coQuyen('signage.manage') && !dangSua ? (
            <button type="button" className="nut nut--chinh" onClick={batDauSua}>
              {vi.bangDienTu.chiTiet.sua}
            </button>
          ) : null}
        </div>
      </header>

      <div className="tab" role="tablist" aria-label={vi.bangDienTu.chiTiet.tab.chung}>
        {(
          [
            ['chung', vi.bangDienTu.chiTiet.tab.chung],
            ['anhChup', vi.bangDienTu.chiTiet.tab.anhChup],
          ] as ['chung' | 'anhChup', string][]
        ).map(([ma, nhan]) => (
          <button
            key={ma}
            type="button"
            role="tab"
            aria-selected={tab === ma}
            className={tab === ma ? 'tab__nut tab__nut--dang-chon' : 'tab__nut'}
            onClick={() => datTab(ma)}
          >
            {nhan}
          </button>
        ))}
      </div>

      {thongBao ? (
        <p role="status" data-vung="thong-bao-luu">
          {thongBao}
        </p>
      ) : null}

      {tab === 'chung' ? (
        <div className="the-thuoc-tinh" data-vung="thuoc-tinh-bang-tin">
          {dangSua ? (
            <>
              <p className="truong">
                <label htmlFor="bdt-huong">{vi.bangDienTu.chiTiet.huongManHinh}</label>
                <select
                  id="bdt-huong"
                  value={huongManHinh}
                  onChange={(e) => datHuongManHinh(e.target.value as 'ngang' | 'doc' | '')}
                >
                  <option value="">{vi.bangDienTu.huongChuaKhaiBao}</option>
                  <option value="ngang">{vi.bangDienTu.huongNgang}</option>
                  <option value="doc">{vi.bangDienTu.huongDoc}</option>
                </select>
              </p>

              <p className="truong">
                <span id="bdt-rong-nhan">{vi.bangDienTu.chiTiet.kichThuocPx}</span>
                <span data-vung="kich-thuoc-px" aria-labelledby="bdt-rong-nhan">
                  <input
                    id="bdt-rong"
                    type="number"
                    min={1}
                    aria-label="Chiều rộng (px)"
                    value={chieuRongPx}
                    onChange={(e) => datChieuRongPx(e.target.value)}
                    style={{ width: '90px' }}
                  />
                  {' × '}
                  <input
                    type="number"
                    min={1}
                    aria-label="Chiều cao (px)"
                    value={chieuCaoPx}
                    onChange={(e) => datChieuCaoPx(e.target.value)}
                    style={{ width: '90px' }}
                  />
                </span>
              </p>

              <div className="man-hinh__hanh-dong">
                <button type="button" className="nut" onClick={() => datDangSua(false)}>
                  {vi.bangDienTu.chiTiet.huy}
                </button>
                <button type="button" className="nut nut--chinh" onClick={() => void luuThuocTinh()}>
                  {vi.bangDienTu.chiTiet.luu}
                </button>
              </div>
            </>
          ) : (
            <dl>
              <dt>{vi.bangDienTu.chiTiet.maThietBi}</dt>
              <dd>
                <code>{tb.cumLoaId}</code>
              </dd>
              <dt>{vi.bangDienTu.chiTiet.nhaCungCap}</dt>
              <dd>{tt.tenNhaCungCap}</dd>
              <dt>{vi.bangDienTu.chiTiet.phuongThucKetNoi}</dt>
              <dd>{vi.thietBi.ketNoi[tt.phuongThucKetNoi as 'truc_tiep' | 'gian_tiep']}</dd>
              <dt>{vi.bangDienTu.chiTiet.huongManHinh}</dt>
              <dd data-vung="huong-man-hinh-hien-thi">{nhanHuong}</dd>
              <dt>{vi.bangDienTu.chiTiet.kichThuocPx}</dt>
              <dd data-vung="kich-thuoc-px-hien-thi">
                {tt.chieuRongPx && tt.chieuCaoPx
                  ? `${tt.chieuRongPx} × ${tt.chieuCaoPx}`
                  : vi.bangDienTu.chiTiet.chuaCo}
              </dd>
              <dt>{vi.bangDienTu.chiTiet.doPhanGiai}</dt>
              <dd>{tt.doPhanGiai ?? vi.bangDienTu.chiTiet.chuaCo}</dd>
              <dt>{vi.bangDienTu.chiTiet.dienTich}</dt>
              <dd>{tt.dienTichM2 ?? vi.bangDienTu.chiTiet.chuaCo}</dd>
              <dt>{vi.bangDienTu.chiTiet.soVungPhat}</dt>
              <dd>{tt.soVungPhat}</dd>
              <dt>{vi.bangDienTu.chiTiet.trongNha}</dt>
              <dd>{tt.trongNha ? 'Có' : 'Không'}</dd>
              <dt>{vi.bangDienTu.chiTiet.capBaoVe}</dt>
              <dd>{tt.capBaoVeVoNgoai ?? vi.bangDienTu.chiTiet.chuaCo}</dd>
              <dt>{vi.bangDienTu.chiTiet.boDieuKhien}</dt>
              <dd>{tt.boDieuKhien ?? vi.bangDienTu.chiTiet.chuaCo}</dd>
            </dl>
          )}
        </div>
      ) : null}

      {tab === 'anhChup' ? (
        <div data-vung="anh-chup-gan-nhat">
          {tt.anhChupGanNhatUrl ? (
            <>
              <img
                src={tt.anhChupGanNhatUrl}
                alt={vi.bangDienTu.chiTiet.tab.anhChup}
                className="anh-chup-man-hinh"
              />
              <p>
                {vi.bangDienTu.chiTiet.anhChupLuc}:{' '}
                {tt.anhChupGanNhatLuc
                  ? new Date(tt.anhChupGanNhatLuc).toLocaleString('vi-VN')
                  : vi.bangDienTu.chiTiet.chuaCo}
              </p>
            </>
          ) : (
            <p>{vi.bangDienTu.chiTiet.anhChupChuaCo}</p>
          )}
          {phien.coQuyen('signage.manage') ? (
            <>
              <label htmlFor="bdt-anh-chup" className="nut">
                {vi.bangDienTu.chiTiet.taiAnhChupMoi}
              </label>
              <input
                id="bdt-anh-chup"
                type="file"
                accept="image/png,image/jpeg"
                className="chi-cho-doc-man-hinh"
                onChange={(e) => {
                  const tep = e.target.files?.[0];
                  if (tep) void bdtGoi.anhChupTaiLen(id, tep).then(() => thuocTinh.taiLai());
                  e.target.value = '';
                }}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
